import { ErrorMonitoringService } from "@/shared/services";
import {
  type OnErrorCallback,
  type OnMessageCallback,
  WorkerManager,
} from "@/shared/webworker";

import {
  SOURCE,
  DataServerCommand,
  TopicIdToMessagePathValues,
  TopicIdToMessagePaths,
} from "./messaging";

export class DataServerManager {
  static async create(
    abortController?: AbortController,
  ): Promise<DataServerManager> {
    abortController ??= new AbortController();

    const worker = new Worker(
      new URL("./DataServer.worker.ts", import.meta.url),
      {
        type: "module",
        name: `DataServer_${crypto.randomUUID()}`,
      },
    );

    // Initialize message handlers
    const onError: OnErrorCallback = (error) => {
      ErrorMonitoringService.captureError(error);
    };

    const onMessage: OnMessageCallback = () => {};

    const workerManager = new WorkerManager({
      worker,
      onError,
      onMessage,
      signal: abortController.signal,
    });

    const response = workerManager.sendCommandAwaitResponse<
      typeof DataServerCommand.Init,
      undefined,
      undefined
    >({
      data: undefined,
      source: SOURCE, // Match the worker's expectation
      type: DataServerCommand.Init,
    });
    await response;
    return new DataServerManager(workerManager, abortController);
  }

  private constructor(
    private workerManager: WorkerManager,
    private abortController: AbortController,
  ) {}

  public dispose(): void {
    this.abortController.abort();
    this.workerManager.dispose();
  }

  async getAllData(
    payload: TopicIdToMessagePaths,
    signal: AbortSignal,
  ): Promise<TopicIdToMessagePathValues> {
    const data = await this.workerManager.sendCommandAwaitResponse<
      typeof DataServerCommand.GetAllData,
      TopicIdToMessagePaths,
      TopicIdToMessagePathValues
    >({
      type: DataServerCommand.GetAllData,
      data: payload,
      source: SOURCE, // Match the worker's expectation
      signal,
    });
    return data;
  }
}
